import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CalendarDays, X } from 'lucide-react';
import { errorMessage, useLocale } from '../../i18n';
import { ConfirmButton } from '../ui/ConfirmButton';
import {
  bookLinkSlot,
  cancelLinkBooking,
  fetchLinkBookings,
  fetchLinkSlots,
  type LinkBooking,
  type LinkRead,
  type LinkSlots,
  type LinkWrite,
} from '../../lib/api/linkBookings';

// ─────────────────────────────────────────────────────────────────────
// «BOOK BANER HOS SENTERET» — a club books a centre's courts through a
// co-operation (slice 4c, 2026-10-20).
//
// The page DRAWS what the server decided and decides nothing: which dates may
// be booked (the centre's window, or the link's longer term with
// extended_horizon — 4A), which times and courts are free (free times only,
// never who holds the rest — 2A), and what the club will be invoiced (the
// partner rate or the list price — 5A; the player owes nothing). The club's
// own bookings here are listed with a cancel, asked in the page first (3A).
//
// Every string is a catalog key; the server's refusal codes become sentences
// through `errorMessage`.
// ─────────────────────────────────────────────────────────────────────

export interface LinkBookingViewProps {
  /** The centre to book at. */
  hostId: string;
  /** Its name, for the heading until the server's answer arrives. */
  hostName: string;
  /** The club this is on behalf of, when the account has several (`?home=`). */
  homeId: string | null;
  onBack: () => void;
  /** Injectable for tests. */
  loadSlots?: typeof fetchLinkSlots;
  loadBookings?: typeof fetchLinkBookings;
  book?: typeof bookLinkSlot;
  cancel?: typeof cancelLinkBooking;
  /** Injectable for tests: a fresh idempotency key per attempt. */
  newRequestId?: () => string;
}

type Notice = { kind: 'ok' | 'error'; text: string } | null;

const kroner = (ore: number) => String(Math.round(ore) / 100);

export function LinkBookingView({
  hostId,
  hostName,
  homeId,
  onBack,
  loadSlots = fetchLinkSlots,
  loadBookings = fetchLinkBookings,
  book = bookLinkSlot,
  cancel = cancelLinkBooking,
  newRequestId = () => `lb_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
}: LinkBookingViewProps) {
  const { t, formatDate } = useLocale();
  const [date, setDate] = useState<string | undefined>(undefined);
  const [durationMin, setDurationMin] = useState<number | undefined>(undefined);
  const [slots, setSlots] = useState<LinkRead<LinkSlots> | null>(null);
  const [mine, setMine] = useState<LinkRead<LinkBooking[]> | null>(null);
  const [generation, setGeneration] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  // THE SERVER'S ANSWER, re-read on every change and after every write — the
  // page never edits its copy to match what it just asked for.
  useEffect(() => {
    let live = true;
    void loadSlots(hostId, { date, durationMin, home: homeId }).then((r) => {
      if (live) setSlots(r);
    });
    void loadBookings(hostId, homeId).then((r) => {
      if (live) setMine(r);
    });
    return () => {
      live = false;
    };
  }, [hostId, homeId, date, durationMin, generation, loadSlots, loadBookings]);
  const reload = () => setGeneration((n) => n + 1);

  const settle = (outcome: LinkWrite, done: string) => {
    if (outcome.state === 'ok') setNotice({ kind: 'ok', text: done });
    else if (outcome.state === 'forbidden')
      setNotice({ kind: 'error', text: t('linkBooking.forbidden') });
    else if (outcome.state === 'refused')
      setNotice({ kind: 'error', text: errorMessage(t, outcome.reason) });
    else setNotice({ kind: 'error', text: t('errors.unknown') });
    reload();
  };

  const value = slots?.state === 'ok' ? slots.value : null;
  const dayLabel = useMemo(
    () => (iso: string) =>
      formatDate(new Date(`${iso}T12:00:00`), { weekday: 'short', day: 'numeric', month: 'short' }),
    [formatDate],
  );

  const doBook = async (court: number, startTime: string) => {
    if (!value || busy) return;
    setBusy(true);
    setNotice(null);
    settle(
      await book(
        hostId,
        {
          court,
          date: value.date,
          startTime,
          durationMin: value.durationMin,
          requestId: newRequestId(),
        },
        homeId,
      ),
      t('linkBooking.booked', {
        court: String(court),
        date: dayLabel(value.date),
        time: startTime,
      }),
    );
    setBusy(false);
  };

  const doCancel = async (b: LinkBooking) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    settle(await cancel(hostId, b.id, homeId), t('linkBooking.cancelled'));
    setBusy(false);
  };

  const body = () => {
    if (slots === null) {
      return (
        <p className="text-sm text-ink-3" data-testid="link-booking-loading">
          {t('linkBooking.loading')}
        </p>
      );
    }
    if (slots.state === 'forbidden') {
      return (
        <p className="text-sm text-danger" data-testid="link-booking-forbidden">
          {t('linkBooking.forbidden')}
        </p>
      );
    }
    if (slots.state === 'refused') {
      return (
        <p className="text-sm text-danger" data-testid="link-booking-refused">
          {errorMessage(t, slots.reason)}
        </p>
      );
    }
    if (slots.state !== 'ok') {
      return (
        <div data-testid="link-booking-unavailable">
          <p className="text-sm text-danger">{t('linkBooking.unavailable')}</p>
          <button type="button" onClick={reload} className="btn-outline text-xs mt-2">
            {t('linkBooking.retry')}
          </button>
        </div>
      );
    }
    const v = slots.value;
    return (
      <div className="space-y-4">
        <p className="text-xs text-ink-3" data-testid="link-booking-terms">
          {v.partnerRate ? t('linkBooking.terms.partner') : t('linkBooking.terms.list')}{' '}
          {t('linkBooking.terms.window', { days: String(v.windowDays) })}
        </p>
        {notice && (
          <p
            role="status"
            data-testid="link-booking-notice"
            className={`text-sm ${notice.kind === 'ok' ? 'text-ink-2' : 'text-danger'}`}
          >
            {notice.text}
          </p>
        )}

        <section className="card p-5" data-testid="link-booking-picker">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
                {t('linkBooking.date')}
              </span>
              <select
                className="input mt-1 w-full"
                value={v.date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="link-booking-date"
              >
                {v.dates.map((d) => (
                  <option key={d} value={d}>
                    {dayLabel(d)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
                {t('linkBooking.duration')}
              </span>
              <select
                className="input mt-1 w-full"
                value={v.durationMin}
                onChange={(e) => setDurationMin(Number(e.target.value))}
                data-testid="link-booking-duration"
              >
                {v.durations.map((m) => (
                  <option key={m} value={m}>
                    {t('linkBooking.minutes', { minutes: String(m) })}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {v.slots.length === 0 ? (
            <p className="mt-4 text-sm text-ink-3" data-testid="link-booking-none">
              {t('linkBooking.noSlots')}
            </p>
          ) : (
            <ul className="mt-4 space-y-2" data-testid="link-booking-slots">
              {v.slots.map((s) => (
                <li
                  key={s.startTime}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line px-3 py-2"
                  data-testid={`link-slot-${s.startTime}`}
                >
                  <span className="text-sm text-ink">
                    {s.startTime}–{s.endTime}
                    <span className="ml-2 text-xs text-ink-3">
                      {t('linkBooking.charge', { amount: kroner(s.chargeOre) })}
                    </span>
                  </span>
                  <span className="flex flex-wrap gap-1.5">
                    {s.courts.map((court) => (
                      <button
                        key={court}
                        type="button"
                        className="btn-outline text-xs"
                        disabled={busy}
                        onClick={() => void doBook(court, s.startTime)}
                        data-testid={`link-book-${s.startTime}-${court}`}
                      >
                        {t('linkBooking.court', { court: String(court) })}
                      </button>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card p-5" data-testid="link-booking-mine">
          <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
            <CalendarDays size={18} /> {t('linkBooking.mine.title', { club: v.home.name })}
          </h2>
          {mine?.state === 'ok' && mine.value.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {mine.value.map((b) => (
                <li
                  key={b.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-line px-3 py-2"
                  data-testid={`link-mine-${b.id}`}
                >
                  <span className="text-sm text-ink">
                    {dayLabel(b.date)} {b.startTime}
                    {b.endTime ? `–${b.endTime}` : ''} ·{' '}
                    {t('linkBooking.court', { court: String(b.court) })}
                    {b.chargeOre !== null && (
                      <span className="ml-2 text-xs text-ink-3">
                        {t('linkBooking.charge', { amount: kroner(b.chargeOre) })}
                      </span>
                    )}
                  </span>
                  <ConfirmButton
                    question={t('linkBooking.cancel.question', {
                      date: dayLabel(b.date),
                      time: b.startTime,
                    })}
                    confirmLabel={t('linkBooking.cancel.confirm')}
                    onConfirm={() => void doCancel(b)}
                    className="inline-flex rounded-full p-1 text-ink-3 hover:text-rose"
                    title={t('linkBooking.cancel.confirm')}
                    disabled={busy}
                  >
                    <X size={14} />
                  </ConfirmButton>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-ink-3" data-testid="link-booking-mine-none">
              {t('linkBooking.mine.none')}
            </p>
          )}
        </section>
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-10 slide-up" data-testid="link-booking-view">
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
        data-testid="link-booking-back"
      >
        <ArrowLeft size={12} /> {t('linkBooking.back')}
      </button>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold mb-1">
          {t('linkBooking.title', { centre: value?.host.name ?? hostName })}
        </h1>
        <p className="text-sm text-ink-3">
          {value ? t('linkBooking.lead', { club: value.home.name }) : t('linkBooking.leadPending')}
        </p>
      </div>
      {body()}
    </div>
  );
}
